import type { ResponseError } from '@app/utils/types'

import SFetch from '@app/utils/sfetch'
import { abort, getSignal } from '@app/adapters/abort'
import { isResponseError } from '@app/utils/predicates'

import api from '../api'
import { CHUNK_FETCH_BATCH_SIZE, MAX_CHUNK_RETRIES } from '../store/constants'
import type { ChunkRow } from '../store/companies/types'

const ABORT_KEY = 'insightsCompaniesOverview'

export type ChunkRowsFetchArgs = {
  requestRef: string
  chunkIndexes: number[]
  signal?: AbortSignal
}

export type ChunkRowsFetchResult = ChunkRow[] | null

const shouldRetryChunk = (
  response: ChunkRowsFetchResult | ResponseError
): boolean => {
  // Only the one-by-one fallback answers no rows. chunk_indexes answers 404.
  if (Array.isArray(response)) {
    return response.length === 0
  }
  if (!isResponseError(response)) return false
  const { status } = response
  // status 0 covers both abort and genuine network failures — bail out in both
  // cases rather than wasting retry budget on requests that can't succeed.
  if (status === 0) return false
  return status === 404 || status >= 500
}

// api/ is still untyped JavaScript, so we wrap it with the precise signature
// we rely on here. Remove the cast once api/ is migrated to TypeScript.
const requestChunkRows = (
  args: ChunkRowsFetchArgs
): Promise<ChunkRowsFetchResult> =>
  (
    api.fetchChunkRows as (
      args: ChunkRowsFetchArgs
    ) => Promise<ChunkRowsFetchResult>
  )(args)

const requestChunkResults = (args: {
  requestRef: string
  chunkIndex: number
  signal?: AbortSignal
}): Promise<ChunkRow['results']> =>
  (
    api.fetchChunkResults as (args: {
      requestRef: string
      chunkIndex: number
      signal?: AbortSignal
    }) => Promise<ChunkRow['results']>
  )(args)

// TODO(razvan): remove with chunk_index when every server accepts
// chunk_indexes. An older server answers 400 to it, so ask for each chunk alone.
async function fetchChunkRowsOneByOne({
  requestRef,
  chunkIndexes,
  signal,
}: ChunkRowsFetchArgs): Promise<ChunkRow[]> {
  const rows = await Promise.all(
    chunkIndexes.map(async (chunkIndex): Promise<ChunkRow | null> => {
      try {
        const results = await requestChunkResults({
          requestRef,
          chunkIndex,
          signal,
        })
        return { chunk_index: chunkIndex, results }
      } catch (error) {
        if (isResponseError(error) && error.status === 404) {
          return null
        }
        throw error
      }
    })
  )
  return rows.filter((row): row is ChunkRow => row !== null)
}

const fetchChunkRows = async (
  args: ChunkRowsFetchArgs
): Promise<ChunkRowsFetchResult> => {
  try {
    return await requestChunkRows(args)
  } catch (error) {
    if (isResponseError(error) && error.status === 400) {
      return fetchChunkRowsOneByOne(args)
    }
    throw error
  }
}

export const fetchChunkRowsWithRetry = SFetch.withRetry<
  [ChunkRowsFetchArgs],
  ChunkRowsFetchResult
>(fetchChunkRows, {
  maxRetries: MAX_CHUNK_RETRIES,
  shouldRetry: shouldRetryChunk,
  getSignal: ([{ signal }]) => signal,
})

export async function fetchChunksInBatches<T>(
  items: T[],
  fetchOne: (item: T) => Promise<unknown>
): Promise<void> {
  for (let i = 0; i < items.length; i += CHUNK_FETCH_BATCH_SIZE) {
    const batch = items.slice(i, i + CHUNK_FETCH_BATCH_SIZE)
    await Promise.all(batch.map((item) => fetchOne(item)))
  }
}

export const abortChunkFetches = (): void =>
  abort(ABORT_KEY, 'Chunk fetches aborted')

export const getChunkFetchSignal = (): AbortSignal | undefined =>
  getSignal(ABORT_KEY)
